import {CompanyEntity, RateModelEntity} from "@ots-frontend/ots-data";
import {UseFormReturnType} from "@mantine/form";
import {useTranslation} from "react-i18next";
import {Button, Divider, Grid, Modal, ModalProps} from "@mantine/core";
import {useCallback} from "react";
import {useTtaDisputeReportForm} from "./useTtaDisputeReportForm";
import {DateInput} from "../../date/DateInput/DateInput";
import {OperatorCompanySelect} from "../../select/entity/CompanySelect/OperatorCompanySelect";
import { TruckCompanyMultiSelect } from "../../select/entity/CompanySelect/TruckCompanyMultiSelect";
import { RateModelSelect } from "../../select/entity/RateModelSelect/RateModelSelect";

export interface TtaDisputeReportFormProps {
  options?: TtaDisputeReportFormOptions;
  defaultValues?: TtaDisputeReportFormDefaultValues;
  onSubmit?: (values: TtaDisputeReportFormValues) => void;
  submitting?: boolean;
}

export interface TtaDisputeReportFormOptions {
  regionId?: string
}

export interface TtaDisputeReportFormValues {
  fromDate: Date
  toDate?: Date
  operator: CompanyEntity | string
  truckCompanies?: (CompanyEntity | string)[]
  rateModel?: RateModelEntity | string
}

export interface TtaDisputeReportFormDefaultValues extends Partial<TtaDisputeReportFormValues> {

}

export interface TtaDisputeReportFormRawValues {
  fromDate?: Date
  toDate?: Date
  operator?: string
  truckCompanies?: string[]
  rateModel?: string
}

export type TtaDisputeReportFormType = UseFormReturnType<TtaDisputeReportFormRawValues, (values: TtaDisputeReportFormRawValues) => TtaDisputeReportFormRawValues>

export const TtaDisputeReportForm = (props: TtaDisputeReportFormProps) => {
  const {
    options,
    defaultValues,
    onSubmit,
    submitting = false
  } = props;

  const {t} = useTranslation(["common"]);

  const form = useTtaDisputeReportForm({
    defaultValues: defaultValues,
  });

  const onFormSubmit = useCallback(async (values: typeof form.values) => {
    if (onSubmit) {
      onSubmit({
        fromDate: values.fromDate!,
        toDate: values.toDate,
        operator: values.operator!,
        truckCompanies: values.truckCompanies,
        rateModel: values.rateModel,
      });
    }
  }, [form, onSubmit]);

  return (
    <form onSubmit={form.onSubmit(onFormSubmit)}>

      <Grid>
        <Grid.Col span={6}>
          <DateInput
            valueFormat="MM/DD/YYYY HH:mm"
            label={t("common.report.ttadispute.form.field.fromdate", "From Date")}
            placeholder={t<string>("common.report.ttadispute.form.field.fromdate", "From Date")}
            clearable
            withAsterisk
            popoverProps={{
              withinPortal: true
            }}
            required
            {...form.getInputProps("fromDate")} />
        </Grid.Col>
        <Grid.Col span={6}>
          <DateInput
            miw={100}
            valueFormat="MM/DD/YYYY HH:mm"
            label={t("common.report.ttadispute.form.field.todate", "To Date")}
            placeholder={t<string>("common.report.ttadispute.form.field.todate", "To Date")}
            clearable
            popoverProps={{
              withinPortal: true
            }}
            {...form.getInputProps("toDate")} />
        </Grid.Col>
      </Grid>

      <OperatorCompanySelect
        label={t("common.report.ttadispute.form.field.operator", "Operator")}
        placeholder={t<string>("common.report.ttadispute.form.field.operator.placeholder", "Operator")}
        selector={{
          regionId: options?.regionId,
        }}
        clearable
        withAsterisk
        required
        {...form.getInputProps("operator")} />

      <RateModelSelect
        label={t("common.report.ttadispute.form.field.ratemodel", "Rate Model")}
        placeholder={t<string>("common.report.ttadispute.form.field.ratemodel.placeholder", "Rate Model")}
        selector={{
          regionId: options?.regionId,
        }}
        clearable
        {...form.getInputProps("rateModel")} />

      <TruckCompanyMultiSelect
        label={t("common.report.ttadispute.form.field.truckcompany", "Haulers")}
        placeholder={t<string>("common.report.ttadispute.form.field.truckcompany.placeholder", "Haulers")}
        selector={{
          regionId: options?.regionId,
        }}
        clearable
        {...form.getInputProps("truckCompanies")} />
      <Divider my="sm" variant="dotted"/>
      <Button type="submit" loading={submitting} fullWidth mt="xl">
        {t("common.action.submit", {ns: "common"})}
      </Button>
    </form>
  );
};

interface TtaDisputeReportFormModalProps extends TtaDisputeReportFormProps, Omit<ModalProps, 'onSubmit'> {

}

export const TtaDisputeReportFormModal = (props: TtaDisputeReportFormModalProps) => {
  const {
    defaultValues,
    onSubmit,
    submitting,
    ...rest
  } = props;

  return (
    <Modal {...rest}>
      <TtaDisputeReportForm defaultValues={defaultValues} onSubmit={onSubmit} submitting={submitting}/>
    </Modal>
  );
};
